import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute';
import { FeatureProtectedRoute } from './components/auth/FeatureProtectedRoute';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ComingSoon from './components/common/ComingSoon';
import Home from './pages/Home';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import YoutubeReel from './pages/YoutubeReel';
import FormConnect from './pages/FormConnect';
import FormView from './pages/FormView';
import FormPreview from './pages/FormPreview';
import SocialMediaContent from './pages/SocialMediaContent';
import DigitalIdentity from './pages/DigitalIdentity';
import DigitalIdentityCreate from './pages/DigitalIdentityCreate';
import DigitalIdentityEdit from './pages/DigitalIdentityEdit';
import DigitalIdentityView from './pages/DigitalIdentityView';
import './styles/App.css';
import './styles/DigitalIdentityCard.css';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureProvider>
          <div className="app-container min-h-screen bg-white dark:bg-gray-900 dark:text-white transition-colors duration-300">
          <Header />
          <main className="main-content">
            <Routes>
              {/* Public routes (no authentication required) */}
              <Route path="/" element={<Home />} />
              
              {/* Auth routes (only accessible when NOT logged in) */}
              <Route element={<PublicOnlyRoute />}>
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
              </Route>
              
              {/* Email verification route (requires auth, but not verification) */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              
              {/* Protected routes (requires authentication AND verification) */}
              <Route element={<ProtectedRoute requireVerification={true} />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/admin" element={<AdminDashboard />} />
                
                {/* YouTube Reel Creator routes - protected by feature flag */}
                <Route element={<FeatureProtectedRoute featureId="youtube-reel" />}>
                  <Route path="/youtube-reel" element={<YoutubeReel />} />
                </Route>
                
                {/* FormConnect routes - protected by feature flag */}
                <Route element={<FeatureProtectedRoute featureId="form-connect" />}>
                  <Route path="/form-connect" element={<FormConnect />} />
                  <Route path="/form/:formId/preview" element={<FormPreview />} />
                </Route>
                
                {/* Social Media Content Generator routes - protected by feature flag */}
                <Route element={<FeatureProtectedRoute featureId="social-media-content" />}>
                  <Route path="/social-media-content" element={<SocialMediaContent />} />
                </Route>
                
                {/* Digital Identity Hub routes - protected by feature flag */}
                <Route element={<FeatureProtectedRoute featureId="digital-identity" />}>
                  <Route path="/digital-identity" element={<DigitalIdentity />} />
                  <Route path="/digital-identity/create" element={<DigitalIdentityCreate />} />
                  <Route path="/digital-identity/:id/edit" element={<DigitalIdentityEdit />} />
                </Route>
                
                {/* Coming Soon routes for disabled features */}
                <Route path="/youtube-reel/coming-soon" element={<ComingSoon featureName="YouTube Reel Creator" />} />
                <Route path="/form-connect/coming-soon" element={<ComingSoon featureName="FormConnect" />} />
                <Route path="/social-media-content/coming-soon" element={<ComingSoon featureName="Social Media Content Generator" />} />
                <Route path="/digital-identity/coming-soon" element={<ComingSoon featureName="Digital Identity Hub" />} />
              </Route>
              
              {/* Public form view route */}
              <Route path="/form/:formId" element={<FormView />} />
              
              {/* Public digital identity view route */}
              <Route path="/digital-identity/:id" element={<DigitalIdentityView />} />
              
              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
        </FeatureProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
